import { useCallback, useEffect, useRef, useState } from 'react'
import {
  LngLatBounds, Map as MapLibreMap, setWorkerUrl,
  type LngLatBoundsLike, type MapMouseEvent, type StyleSpecification,
} from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
// MapLibre parses vector data in a worker. Vite bundles the app into one
// chunk, which leaves the library with no URL to spawn it from, so point it at
// the worker Vite emits for us — without this the basemap draws but no plot
// ever appears.
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'
import { Box } from '@mui/material'
import { formatLngLat, formatScale } from '../lib/format'
import { useBasemapStyles } from '../api/hooks'
import { apiUrl, currentAuthorization, getConfig } from '../api/client'
import type { BasemapStyle } from '../api/types'
import { tokens } from '../theme'

setWorkerUrl(maplibreWorkerUrl)

export type Basemap = 'map' | 'satellite'

/**
 * Both basemaps live in one style and are toggled by visibility. Swapping
 * styles would tear down every data layer the screens add on top.
 *
 * Which providers those are is the API's business, not this component's —
 * see the note on the tile proxy below.
 */
/**
 * Tiles come from this API, not from a provider named here — unless the
 * host configured that style's URL (`basemaps`, see useBasemapStyles).
 *
 * Naming the provider in the client is only safe while it needs no
 * credential. The moment one does, the key is in the bundle — and in the
 * embeddable package, inside a partner's application, where we can neither
 * rotate nor revoke it. `GET /basemap` describes the styles and the API
 * proxies the tiles, so the credential stays on the server. A configured
 * URL is the host's choice to skip that, for keyless tiles or a key its
 * provider restricts to its domains.
 *
 * Offline, the tiles simply fail and the plots stay readable on the warm
 * background.
 */
const FALLBACK_ATTRIBUTION = 'Esri, HERE, Garmin, © OpenStreetMap contributors'

/** Used until GET /basemap answers, so the map has something to render. */
const STYLE: StyleSpecification = {
  version: 8,
  sources: {},
  layers: [{ id: 'page', type: 'background', paint: { 'background-color': '#eceee8' } }],
}

const SOURCE_FOR: Record<string, string> = {
  light: 'basemap-light',
  labels: 'basemap-labels',
  satellite: 'basemap-satellite',
}

const OPACITY: Record<string, number> = { light: 0.9, labels: 0.75, satellite: 0.95 }

/** Adds the API's styles to a map that has already loaded. */
function applyBasemap(map: MapLibreMap, styles: BasemapStyle[], basemap: Basemap) {
  for (const style of styles) {
    const id = SOURCE_FOR[style.id]
    if (!id || map.getSource(id)) continue
    map.addSource(id, {
      type: 'raster',
      tiles: style.tiles,
      tileSize: style.tileSize,
      maxzoom: style.maxzoom,
      attribution: style.attribution || FALLBACK_ATTRIBUTION,
    })
    map.addLayer(
      {
        id,
        type: 'raster',
        source: id,
        layout: {
          visibility:
            (basemap === 'satellite') === (style.id === 'satellite') || style.id === 'labels'
              ? 'visible'
              : 'none',
        },
        paint: { 'raster-opacity': OPACITY[style.id] ?? 1 },
      },
      // Beneath everything the application draws.
      map.getStyle().layers.find((l) => l.id !== 'page' && !l.id.startsWith('basemap-'))?.id,
    )
  }
}

export interface MapCanvasProps {
  /** Called once the style has loaded; add sources and layers here. */
  onReady?: (map: MapLibreMap) => void
  /**
   * Called as the map is torn down (the canvas unmounting). A parent that
   * kept the map from onReady must drop it here: calling into a removed map
   * throws, and a throw in an effect blanks the whole page.
   */
  onRemove?: () => void
  onMapClick?: (event: MapMouseEvent) => void
  basemap?: Basemap
  initialBounds?: LngLatBoundsLike
  initialCentre?: [number, number]
  initialZoom?: number
  interactive?: boolean
  children?: React.ReactNode
  /** Corner readout, hidden on the smaller inline maps. */
  showReadout?: boolean
  cursor?: string
}

export function MapCanvas({
  onReady, onRemove, onMapClick, basemap = 'map', initialBounds, initialCentre = [-6.6014, 5.7988],
  initialZoom = 12, interactive = true, children, showReadout = true, cursor,
}: MapCanvasProps) {
  const container = useRef<HTMLDivElement>(null)
  const map = useRef<MapLibreMap | null>(null)
  const [readout, setReadout] = useState('')

  // Which providers to use is the API's answer; the map is created before it
  // arrives, so the styles are applied whenever both are ready.
  const { data: basemapStyles } = useBasemapStyles()
  const stylesRef = useRef<BasemapStyle[] | null>(null)
  const basemapRef = useRef(basemap)
  const loadedRef = useRef(false)
  basemapRef.current = basemap
  stylesRef.current = basemapStyles ?? null

  /**
   * Whichever arrives last — the map's load event or the API's answer —
   * triggers this. Tracking the load explicitly rather than asking
   * `isStyleLoaded()`, which answers about the style rather than about
   * whether it is safe to add to it.
   */
  const applyIfReady = useCallback(() => {
    const instance = map.current
    if (!instance || !loadedRef.current || !stylesRef.current) return
    applyBasemap(instance, stylesRef.current, basemapRef.current)
  }, [])

  useEffect(applyIfReady, [basemapStyles, applyIfReady])

  useEffect(() => {
    if (!container.current || map.current) return
    const instance = new MapLibreMap({
      container: container.current,
      style: STYLE,
      center: initialCentre,
      zoom: initialZoom,
      interactive,
      attributionControl: false,
      // Keeps areas honest at this latitude and avoids a tilted, prettier lie.
      pitchWithRotate: false,
      dragRotate: false,
      // Tiles come from this API and it requires a caller, so they carry the
      // same credential every other request does. Synchronous by necessity:
      // MapLibre cannot await here.
      transformRequest: (url) => {
        if (!url.startsWith(apiUrl(''))) return { url }
        const authorization = currentAuthorization()
        // MapLibre accepts only these two; 'omit' is its default anyway.
        const credentials = getConfig().credentials === 'include' ? 'include' : 'same-origin'
        return {
          url,
          credentials,
          ...(authorization ? { headers: { authorization } } : {}),
        }
      },
    })
    map.current = instance

    instance.on('load', () => {
      loadedRef.current = true
      // Basemap first, so its layers sit beneath whatever onReady adds.
      applyIfReady()
      if (initialBounds) instance.fitBounds(initialBounds, { padding: 64, animate: false })
      onReady?.(instance)
    })

    const update = () => {
      const c = instance.getCenter()
      setReadout(`${formatLngLat(c.lng, c.lat)} · ${formatScale(instance.getZoom(), c.lat)}`)
    }
    instance.on('move', update)
    update()

    return () => {
      onRemove?.()
      instance.remove()
      map.current = null
    }
    // The map is created once; every later change goes through imperative calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const instance = map.current
    if (!instance || !instance.isStyleLoaded()) return
    const showMap = basemap === 'map'
    // The layers arrive with GET /basemap, so they may not exist yet.
    const show = (id: string, visible: boolean) => {
      if (instance.getLayer(id)) {
        instance.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none')
      }
    }
    show('basemap-light', showMap)
    show('basemap-labels', showMap)
    show('basemap-satellite', !showMap)
  }, [basemap])

  useEffect(() => {
    const instance = map.current
    if (!instance || !onMapClick) return
    instance.on('click', onMapClick)
    return () => {
      instance.off('click', onMapClick)
    }
  }, [onMapClick])

  useEffect(() => {
    const canvas = map.current?.getCanvas()
    if (canvas && cursor !== undefined) canvas.style.cursor = cursor
  }, [cursor])

  return (
    <Box sx={{ position: 'relative', flex: 1, minWidth: 0, backgroundColor: '#eceee8', overflow: 'hidden' }}>
      <Box ref={container} sx={{ position: 'absolute', inset: 0 }} />
      {children}
      {showReadout && (
        <Box
          sx={{
            position: 'absolute', right: 16, bottom: 16, pointerEvents: 'none',
            font: `400 11px ${tokens.font.mono}`, color: 'text.secondary',
            backgroundColor: 'rgba(250,250,248,.85)', px: 2, py: 1,
            borderRadius: `${tokens.radius.sm}px`, fontVariantNumeric: 'tabular-nums',
          }}
        >
          {readout}
        </Box>
      )}
    </Box>
  )
}

/** A floating map control: surface, hairline, and the only place shadows appear. */
export function MapControl({
  children, sx, ...rest
}: React.ComponentProps<typeof Box>) {
  return (
    <Box
      sx={{
        position: 'absolute', display: 'flex', backgroundColor: 'background.paper',
        border: `1px solid ${tokens.color.ink200}`, borderRadius: `${tokens.radius.md}px`,
        boxShadow: tokens.shadow[2], overflow: 'hidden', ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  )
}

/** The Basemap / Satellite (/ Land use) switch. */
export function BasemapSwitch<T extends string>({
  value, options, onChange, sx, label = 'Basemap',
}: {
  value: T
  options: { value: T; label: string }[]
  onChange: (v: T) => void
  sx?: React.ComponentProps<typeof Box>['sx']
  /** The group's name for screen readers, in the reader's language. */
  label?: string
}) {
  return (
    <MapControl
      role="group"
      aria-label={label}
      sx={{ top: 16, right: 16, font: `500 12px ${tokens.font.sans}`, ...sx }}
    >
      {options.map((o, i) => (
        <Box
          key={o.value}
          component="button"
          type="button"
          onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
          sx={{
            border: 0, cursor: 'pointer', px: 3, py: 2.25, font: 'inherit',
            borderLeft: i > 0 ? `1px solid ${tokens.color.ink200}` : 0,
            backgroundColor: value === o.value ? tokens.color.ink100 : 'transparent',
            color: value === o.value ? 'text.primary' : 'text.secondary',
            transition: `background ${tokens.motion.dur.fast}ms ${tokens.motion.easeStd}`,
            '&:hover': { backgroundColor: tokens.color.ink100 },
          }}
        >
          {o.label}
        </Box>
      ))}
    </MapControl>
  )
}

/** Zoom buttons, matching the 1b mockup. */
export function ZoomControl({ map, sx, labels = { zoomIn: 'Zoom in', zoomOut: 'Zoom out' } }: {
  map: MapLibreMap | null
  sx?: React.ComponentProps<typeof Box>['sx']
  /** The buttons' names for screen readers, in the reader's language. */
  labels?: { zoomIn: string; zoomOut: string }
}) {
  const button = {
    width: 32, height: 32, border: 0, cursor: 'pointer', backgroundColor: 'transparent',
    display: 'grid', placeItems: 'center', font: `400 18px ${tokens.font.sans}`, color: 'text.secondary',
    '&:hover': { backgroundColor: tokens.color.ink100 },
  } as const
  return (
    <MapControl sx={{ top: 16, right: 16, flexDirection: 'column', ...sx }}>
      <Box
        component="button"
        type="button"
        aria-label={labels.zoomIn}
        onClick={() => map?.zoomIn()}
        sx={{ ...button, borderBottom: `1px solid ${tokens.color.ink200}` }}
      >
        +
      </Box>
      <Box component="button" type="button" aria-label={labels.zoomOut} onClick={() => map?.zoomOut()} sx={button}>
        &minus;
      </Box>
    </MapControl>
  )
}

/** Fits a feature collection, leaving room for the panels either side. */
export function fitToFeatures(
  map: MapLibreMap,
  collection: GeoJSON.FeatureCollection | null | undefined,
  padding: number | { top: number; bottom: number; left: number; right: number } = 64,
) {
  if (!collection?.features?.length) return
  const bounds = new LngLatBounds()
  let any = false
  for (const feature of collection.features) {
    forEachPosition(feature.geometry, ([lng, lat]) => {
      bounds.extend([lng, lat])
      any = true
    })
  }
  if (any) map.fitBounds(bounds, { padding, maxZoom: 17, duration: 400 })
}

export function forEachPosition(
  geometry: GeoJSON.Geometry | null | undefined,
  visit: (position: [number, number]) => void,
) {
  if (!geometry) return
  const walk = (coords: unknown): void => {
    if (!Array.isArray(coords)) return
    if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      visit([coords[0] as number, coords[1] as number])
      return
    }
    for (const c of coords) walk(c)
  }
  if (geometry.type === 'GeometryCollection') {
    for (const g of geometry.geometries) forEachPosition(g, visit)
    return
  }
  walk((geometry as { coordinates?: unknown }).coordinates)
}
